/**
 * The small "Open in a new window" icon in a big module's own header
 * (Schedule, Chart, Imaging, Ledger, Perio, Phones, Analytics). Zac could not
 * find pop-outs in his r57 walkthrough: the only way in was the top-bar Pop
 * out menu. This opens the page on screen in the module's pop-out window, the
 * same as that menu's "Pop out <page>" entry, and hides itself inside a
 * pop-out (it is one already).
 *
 * Works the same in a browser (window.open) and in the desktop app (the
 * shell's window-open handler turns it into a real app window).
 */
import { useLocation } from '@tanstack/react-router';
import { IconButton } from '@velora/ui';
import { AppWindow } from 'lucide-react';
import { useOfficeScope } from '../office/OfficeScope.js';
import { openPopout } from './open.js';
import { POP_PARAM, popoutKeyFor, popoutSpec, readPopoutQuery } from './popout.js';
import { readPinnedDefault } from './workspace.js';
import './windows.css';

/** Path plus query of the page on screen, without the office selector or pop-out params. */
export function pageHref(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  params.delete('office');
  params.delete(POP_PARAM);
  params.delete('pin');
  const q = params.toString();
  return `${pathname}${q ? `?${q}` : ''}`;
}

/**
 * "Open in new window" for a link (sidebar item, module tab): opens `href` in
 * its module's pop-out window, or its own window for any other page.
 */
export function useOpenInWindow(): (href: string, id: string, label: string) => void {
  const office = useOfficeScope();
  return (href, id, label) => {
    const key = popoutKeyFor(href, id);
    const params = new URL(href, 'http://x');
    params.searchParams.delete('office');
    void openPopout({
      key,
      label: popoutSpec(key)?.label ?? label,
      href: `${params.pathname}${params.search}`,
      office: office.param,
      pinned: readPinnedDefault(key),
    });
  };
}

/** Whether this window is itself a pop-out (no "open in new window" there). */
export function inPopout(): boolean {
  return typeof window !== 'undefined' && readPopoutQuery(window.location.search) !== null;
}

export function PopoutHereButton({
  module: key,
  className,
}: {
  /** The module key (popout.ts POPOUT_MODULES). */
  module: string;
  className?: string;
}) {
  const location = useLocation();
  const office = useOfficeScope();
  if (typeof window === 'undefined' || inPopout()) return null;
  const spec = popoutSpec(key);
  const label = spec?.label ?? key;
  return (
    <IconButton
      label={`Open ${label} in a new window`}
      icon={<AppWindow size={15} aria-hidden />}
      size="sm"
      variant="ghost"
      className={['vel-popout-here', className].filter(Boolean).join(' ')}
      data-testid={`popout-here-${key}`}
      onClick={() =>
        void openPopout({
          key,
          label,
          href: pageHref(location.pathname, window.location.search),
          office: office.param,
          pinned: readPinnedDefault(key),
        })
      }
    />
  );
}
